import React, { useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { SilverSunnStripeSubscription } from "@switcherstudio/switcher-api-client";
import { DateTimePicker } from "components/inputs/datepicker/DateTimePicker";
import { displayDate } from "helpers/time";
import styles from "./index.module.scss";
import dayjs from "dayjs";
import { usePauseStatus } from "hooks/usePauseStatus";
import { exists } from "helpers/booleans";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { displayAmount } from "helpers/stripe";
import { ListButton } from "components/buttons/ListButton";
import { SkeletonTextLoader } from "components/skeleton/SkeletonTextLoader";

export interface PauseStepProps {
    currentSubscription: SilverSunnStripeSubscription;
    pauseDate: string;
    setPauseDate: (date: string) => void;
    /**
     * Function to call when the user clicks to reactivate their plan.
     * This should handle the logic for reactivating the plan.
     */
    cancelPause: () => void;
    /**
     * Function to call when the user clicks to cancel their plan.
     * This should handle the logic for canceling the plan.
     */
    cancelPlan: () => void;
}

export const PauseStep: React.FC<PauseStepProps> = ({
    currentSubscription,
    pauseDate,
    setPauseDate,
    cancelPause,
    cancelPlan
}) => {
    const { t, i18n } = useTranslation();

    const {
        pausedPlanName,
        userIsPaused,
        userWillPause,
        userIsOrWillPause,
        pauseBeginDate,
        pauseEndDate,
        pausedPlanId
    } = usePauseStatus();

    const { data: pausedPlan } = useSwitcherClient(
        (client) => client.stripePrices_GetPriceInfo,
        {
            suppressErrorBoundary: true,
            requestImmediately: exists(pausedPlanId),
            args: [pausedPlanId]
        }
    );

    const minPauseDate = useMemo(() => {
        return dayjs(currentSubscription?.ActiveUntil).add(30, "day").toDate();
    }, [currentSubscription?.ActiveUntil]);

    useEffect(() => {
        if (!pauseDate) {
            setPauseDate(pauseEndDate ?? minPauseDate.toUTCString());
        }
    }, [minPauseDate, setPauseDate, pauseDate, pauseEndDate]);

    const displayedMonthlyAmount = useMemo(() => {
        if (isNaN(pausedPlan?.Amount)) {
            return <SkeletonTextLoader width="6rem" />;
        }
        return (
            displayAmount(pausedPlan?.Amount, {
                signed: true,
                compact: true
            }) +
            "/" +
            t("misc:month")
        );
    }, [pausedPlan, t]);

    const appliedDate = useMemo(() => {
        return pauseDate ? dayjs(pauseDate).toDate() : minPauseDate;
    }, [pauseDate, minPauseDate]);

    return (
        <div className={styles["pause-step"]}>
            <div className={styles["date-picker-container"]}>
                {userIsOrWillPause && (
                    <div className={styles["date-picker-header"]}>
                        <h3>
                            {pausedPlanName}{" "}
                            {userIsPaused && "(" + t("pause-copy:paused") + ")"}
                        </h3>
                        <p>
                            {userWillPause
                                ? t(
                                      "subscription:pause-step:subheader:will-pause",
                                      {
                                          date: displayDate(
                                              pauseBeginDate,
                                              "en-US",
                                              "dddd MMMM D, YYYY"
                                          )
                                      }
                                  )
                                : displayedMonthlyAmount}
                        </p>
                        <hr />
                    </div>
                )}
                <label htmlFor="pause-until-date">
                    {t("subscription:reactivation-date")}
                </label>
                <DateTimePicker
                    id="pause-until-date"
                    datePickerProps={{
                        selected: appliedDate,
                        minDate: minPauseDate,
                        maxDate: null,
                        openToDate: appliedDate
                    }}
                    dateOnly={true}
                    error={""}
                    onChange={(date: Date) => {
                        setPauseDate(date.toUTCString());
                    }}
                />
            </div>

            {!userIsPaused && (
                <p className={styles["disclaimer"]}>
                    <Trans
                        i18nKey={"subscription:pause-disclaimer"}
                        components={{
                            span: <span />,
                            strong: <strong />
                        }}
                        values={{
                            targetDate: displayDate(
                                pauseBeginDate ??
                                    dayjs(currentSubscription?.ActiveUntil)
                                        .add(-1, "day")
                                        .toDate(),
                                i18n.language,
                                "dddd, MMMM D YYYY"
                            )
                        }}
                    />
                </p>
            )}

            {userIsOrWillPause && (
                <>
                    <ListButton onClick={cancelPause}>
                        {userWillPause
                            ? t(`subscription:cancel-pause`)
                            : t(`subscription:reactivate`)}
                    </ListButton>

                    <ListButton onClick={cancelPlan} variant="danger">
                        {t(`subscription:cancel-plan`)}
                    </ListButton>
                </>
            )}
        </div>
    );
};
